import { useHistory, useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Avatar, Box, IconButton, Toolbar, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  Auth_IsImpersonatingDocument,
  PortalPermissions,
  useAuth_StopImpersonatingMutation,
  usePortal_GetPortalByIdQuery,
} from 'queries';

import SeePlansButton from 'layouts/home-layout/components/see-plans-button';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useCurrentUser from 'utils/useCurrentUser';
import useHasPortalPermission from 'utils/useHasPortalPermission';
import useIsImpersonating from 'utils/useIsImpersonating';

import Account from './account';
import ActionNeededEstateWorkItemsCount from './action-needed-estate-work-items-count';
import PortalName from './portal-name';
import Profile from './profile';

type Props = {
  showPortalName: boolean;
  isLeftNavBarOpen: boolean;
  setIsLeftNavBarOpen: (val: boolean) => void;
  isInviteTeamMemberBannerVisible: boolean;
  setIsInviteTeamMemberBannerVisible: (val: boolean) => void;
  onAddTeamMemberClicked: () => void;
};

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#1C2541',
    color: '#FFF',
  },
  title: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
  },
  logo: {
    height: 22,
    marginRight: 10,
    cursor: 'pointer',
  },
  portalLogo: {
    height: 40,
    width: 40,
    marginRight: 10,
    [theme.breakpoints.down('xs')]: {
      height: 28,
      width: 28,
    },
    background: 'white',
  },
  pointer: {
    cursor: 'pointer',
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  inviteTeamBar: {
    background: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: theme.spacing(4.5),
    fontSize: theme.spacing(1.75),
    position: 'relative',
    color: '#1C2541',
  },
  closeInviteTeamBanner: {
    position: 'absolute',
    right: theme.spacing(1),
    color: '#1C2541',
  },
  infoIcon: {
    marginRight: theme.spacing(0.5),
    marginBottom: '-3px',
  },
  inviteLink: {
    textDecoration: 'underline',
    cursor: 'pointer',
    marginRight: theme.spacing(0.5),
  },
}));

const NavBar = ({
  showPortalName,
  isLeftNavBarOpen,
  setIsLeftNavBarOpen,
  setIsInviteTeamMemberBannerVisible,
  isInviteTeamMemberBannerVisible,
  onAddTeamMemberClicked,
}: Props) => {
  const history = useHistory();
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const currentUser = useCurrentUser();
  const isImpersonating = useIsImpersonating();
  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const hasPortalPermissionUser = useHasPortalPermission(PortalPermissions.User);

  const { data: portalData, loading: portalLoading } = usePortal_GetPortalByIdQuery({
    skip: !portalId,
    variables: { id: portalId },
    onError: (e) => {
      showAlert({
        message: 'Error getting portal by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [stopImpersonating] = useAuth_StopImpersonatingMutation({
    refetchQueries: [{ query: Auth_IsImpersonatingDocument }],
    onCompleted: (data) => {
      let redirectUrl = '/home';
      if (data.data.output) {
        try {
          const output = JSON.parse(data.data.output);
          if (output.returnUrl) {
            redirectUrl = output.returnUrl;
          }
        } catch (e) {}
      }

      // react history.push() doesn't work because a page reload is needed for impersonate to take effect
      // TODO: Remove the need of a page refresh for impersonate to take effect.
      window.location.href = redirectUrl;
    },
    onError: (e) => {
      showAlert({
        message: 'Error stopping impersonating user: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleStopImpersonating = async () => {
    await stopImpersonating();
  };

  const renderDashboardTitle = () => {
    if (showPortalName) {
      return <PortalName />;
    }

    if (history.location.pathname.includes('/my-profile')) {
      return <Profile />;
    }

    if (currentUser?.isSuperAdmin) {
      return 'Admin Dashboard';
    }

    return '';
  };

  const getPortalLogo = () => {
    if (portalLoading) {
      return null;
    }

    if (portalData?.data?.logoUrl) {
      return (
        <Avatar
          onClick={() => {
            if (portalId) {
              history.push(`/home/${portalId}/home`);
              return;
            }

            history.push('/home');
          }}
          alt="portal-logo"
          src={portalData.data?.logoUrl}
          className={classes.portalLogo}
        />
      );
    }

    return (
      <img
        src="/trustate-logo-white.svg"
        onClick={() => {
          if (portalId) {
            history.push(`/home/${portalId}/home`);
            return;
          }

          history.push('/home');
        }}
        alt="trustate-logo"
        className={classes.logo}
      />
    );
  };

  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.root}>
        {isInviteTeamMemberBannerVisible && (
          <Box className={classes.inviteTeamBar}>
            <InfoOutlinedIcon fontSize="small" className={classes.infoIcon} />
            <Typography
              component="span"
              className={classes.inviteLink}
              onClick={onAddTeamMemberClicked}
            >
              Invite your team members
            </Typography>{' '}
            <Typography> and start collaborating.</Typography>
            <IconButton
              onClick={() => setIsInviteTeamMemberBannerVisible(false)}
              className={classes.closeInviteTeamBanner}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        <Toolbar>
          {portalId && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setIsLeftNavBarOpen(!isLeftNavBarOpen)}
              edge="start"
              className={clsx(classes.menuButton)}
            >
              <MenuIcon />
            </IconButton>
          )}

          <div className={classes.title}>
            {getPortalLogo()}

            <Typography
              variant="h6"
              onClick={() => {
                if (portalId) {
                  history.push(`/home/${portalId}/home`);
                  return;
                }

                history.push('/home');
              }}
              data-cy="sa-dash"
              className={classes.pointer}
            >
              {renderDashboardTitle()}
            </Typography>
            {isImpersonating && (
              <Typography
                variant="caption"
                color="error"
                onClick={handleStopImpersonating}
                data-cy="stop-impersonating"
                style={{ marginLeft: 10 }}
                className={classes.pointer}
              >
                Back to Admin Dashboard
              </Typography>
            )}
          </div>
          {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
            <>
              <SeePlansButton />
              <ActionNeededEstateWorkItemsCount />
            </>
          )}

          <Account />
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default NavBar;
